import { axiosInstance } from "../lib/axios";

// ── Public Reviews ────────────────────────────────────────────────────────────

export interface PublicReviewDto {
  id: number;
  customerName?: string;
  overallRating: number;
  foodQuality: number;
  service: number;
  appExperience: number;
  cleanliness: number;
  note?: string;
  issues: string[];
  submittedAt: string;
  hasVerifiedOrder: boolean;
}

// ──────────────────────────────────────────────────────────────────────────────

export interface RestaurantDetails {
  id: number;
  name: string;
  address?: string;
  location?: string; 
  phone?: string;
  email?: string;
  website?: string;
  isActive: boolean;
  hasTableService?: boolean;
  hasOnlineOrdering?: boolean;
  logo?: string | null;
  displayName?: string;

  //  Enhanced Customer-Facing Information
  tagline?: string;
  description?: string;
  cuisineType?: string;
  cuisine?: string[]; // Backward compatibility
  priceRange?: "₹" | "₹₹" | "₹₹₹" | "₹₹₹₹";
  openingHours?: string;

  //  Service Information
  deliveryTime?: number;
  deliveryFee?: number;
  minOrderAmount?: number;

  //  Features & Amenities
  specialFeatures?: string;
  offers?: Array<{
    title: string;
    description: string;
    promoCode: string;
    isActive: boolean;
    validFrom?: string;
    validUntil?: string;
  }>;
  features?: {
    wifi?: boolean;
    parking?: boolean;
    delivery?: boolean;
    takeaway?: boolean;
  };

  // Visual Assets
  bannerImage?: string | null;
  aboutImage?: string | null;
  images?: string[];
  galleryImages?: string[];
  featuredDishIds?: number[];
  featuredDishes?: FeaturedDish[];

  // Zillout-Style Vibe & Ambience (Experience Discovery)
  vibeTags?: string[];  // e.g., ['rooftop', 'romantic', 'party']
  ambienceImages?: string[];  // Interior/vibe photos distinct from food gallery 

  // Social media links 
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  // Performance Data
  rating?: number;
  averageRating?: number;
  reviewCount?: number;
  topReviews?: PublicReviewDto[];
  totalReviews?: number;

  // Ownership & Audit Fields (from API response)
  ownerId?: number;
  ownerName?: string;
  createdAt?: string;
  updatedAt?: string;

  // UI preferences
  template?: string;

  // Order Settings
  orderSettings?: {
    allowQrOrdering: boolean;
  };

  // Tax Settings (from restaurant's configured settings)
  taxSettings?: {
    enabled: boolean;
    label: string;
    percentage: number;
  };
}

// Menu API specific interfaces
export interface RestaurantMenuItem {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  isAvailable: boolean;
  availabilityReason: string | null;
  preparationTime: number;
  imageUrl?: string;
  availableServings?: number | null;
  canBePrepared: boolean;
  metadata: {
    isVegetarian: boolean;
    isVegan: boolean;
    isSpicy: boolean;
    allergens: string;
    calories: number;
  };
  insights: {
    totalOrders: number;
    popularityScore: number;
  };
}

export interface RestaurantMenuCategory {
  name: string;
  itemCount: number;
}

export interface RestaurantMenuResponse {
  restaurantId: number;
  restaurantName: string;
  menuItems: RestaurantMenuItem[];
  categories: RestaurantMenuCategory[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  hasMorePages: boolean;
  currency: string;
  lastUpdated: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// Customer order interfaces for public API
export interface CustomerOrderItem {
  menuItemId: number;
  quantity: number;
  specialInstructions?: string;
}

export interface CustomerOrderRequest {
  tableId?: number | string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  items: CustomerOrderItem[];
  specialInstructions?: string;
  discountAmount?: number;
  loyaltyPointsRedeemed?: number; // Added
}

export interface CustomerOrderItem {
  menuItemId: number;
  menuItemName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  specialInstructions?: string;
}

// Top-Up Modules Interfaces
export interface TopUpModulesDto {
  moduleKeys: string[];
}

export interface RestaurantTopUpResponse {
  restaurantId: number;
  restaurantName: string;
  topUpModules: string[];
  addedAt?: string;
}

export interface CustomerOrderResponse {
  orderId: number;
  orderNumber: string;
  restaurantId: number;
  restaurantName: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  tableNumber?: string;
  items: CustomerOrderItem[];
  totalItems: number;
  subTotal: number;
  loyaltyPointsRedeemed?: number; // Added
  loyaltyDiscountValue?: number; // Added
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  createdAt: string;
  estimatedCompletionTime?: string;
  orderStatus: string;
  orderType: string;
  placedAt: string;
  specialInstructions?: string;
}

/**
 * Get restaurant details by ID
 */
export const getRestaurantById = async (
  restaurantId: number
): Promise<RestaurantDetails> => {
  try {
    const response = await axiosInstance.get<ApiResponse<RestaurantDetails>>(
      `/api/public/restaurant/${restaurantId}`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(
        response.data.message || "Failed to fetch restaurant details"
      );
    }
  } catch (error: any) {
    console.error("Error fetching restaurant details:", error);

    // Handle specific error cases
    if (error.response?.status === 404) {
      throw new Error("Restaurant not found");
    } else if (error.response?.status === 403) {
      throw new Error("Access denied to restaurant details");
    } else {
      throw new Error("Failed to fetch restaurant details");
    }
  }
};

/**
 * Get restaurant basic info (lightweight version)
 */
export const getRestaurantInfo = async (restaurantId: number) => {
  try {
    const response = await axiosInstance.get(
      `/api/public/restaurant/${restaurantId}/info`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching restaurant info:", error);
    throw error;
  }
};

/**
 * Check if restaurant is active and accepting orders
 */
export const checkRestaurantStatus = async (restaurantId: number) => {
  try {
    const response = await axiosInstance.get(
      `/api/public/restaurant/${restaurantId}/status`
    );
    return response.data;
  } catch (error) {
    console.error("Error checking restaurant status:", error);
    throw error;
  }
};

/**
 * Get restaurant menu items
 */
export const getRestaurantMenu = async (
  restaurantId: number
): Promise<RestaurantMenuItem[]> => {
  try {
    const response = await axiosInstance.get<
      ApiResponse<RestaurantMenuResponse>
    >(`/api/public/restaurant/${restaurantId}/menu`);

    if (response.data.success && response.data.data) {
      return response.data.data.menuItems;
    } else {
      throw new Error(
        response.data.message || "Failed to fetch restaurant menu"
      );
    }
  } catch (error: any) {
    console.error("Error fetching restaurant menu:", error);

    if (error.response?.status === 404) {
      throw new Error("Menu not found for this restaurant");
    } else if (error.response?.status === 403) {
      throw new Error("Access denied to restaurant menu");
    } else {
      throw new Error("Failed to fetch restaurant menu");
    }
  }
};

/**
 * Get complete restaurant menu data including currency and other metadata
 */
export const getRestaurantMenuData = async (
  restaurantId: number
): Promise<RestaurantMenuResponse> => {
  try {
    const response = await axiosInstance.get<
      ApiResponse<RestaurantMenuResponse>
    >(`/api/public/restaurant/${restaurantId}/menu`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(
        response.data.message || "Failed to fetch restaurant menu data"
      );
    }
  } catch (error: any) {
    console.error("Error fetching restaurant menu data:", error);

    if (error.response?.status === 404) {
      throw new Error("Menu not found for this restaurant");
    } else if (error.response?.status === 403) {
      throw new Error("Access denied to restaurant menu");
    } else {
      throw new Error("Failed to fetch restaurant menu data");
    }
  }
};

/**
 * Get restaurant menu categories
 */
export const getRestaurantCategories = async (
  restaurantId: number
): Promise<RestaurantMenuCategory[]> => {
  try {
    const response = await axiosInstance.get<
      ApiResponse<RestaurantMenuResponse>
    >(`/api/public/restaurant/${restaurantId}/menu`);

    if (response.data.success && response.data.data) {
      return response.data.data.categories;
    } else {
      throw new Error(
        response.data.message || "Failed to fetch restaurant categories"
      );
    }
  } catch (error: any) {
    console.error("Error fetching restaurant categories:", error);

    if (error.response?.status === 404) {
      throw new Error("Categories not found for this restaurant");
    } else if (error.response?.status === 403) {
      throw new Error("Access denied to restaurant categories");
    } else {
      throw new Error("Failed to fetch restaurant categories");
    }
  }
};

/**
 * Fetch order details for a specific order (public endpoint)
 * Used by customers to view their order confirmation and track status
 * No authentication required
 * @param restaurantId - The restaurant ID
 * @param orderId - The order ID to retrieve
 * @returns Order details with items, totals, and status
 */
export const getCustomerOrderDetails = async (
  restaurantId: number,
  orderId: number
): Promise<CustomerOrderResponse> => {
  try {
    const response = await axiosInstance.get<
      ApiResponse<CustomerOrderResponse>
    >(`/api/public/restaurant/${restaurantId}/order/${orderId}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch order details");
    }
  } catch (error: any) {
    console.error("Error fetching order details:", error);

    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || "Invalid request");
    } else if (error.response?.status === 404) {
      throw new Error("Order not found. It may have been deleted or the link is invalid.");
    } else if (error.response?.status === 503) {
      throw new Error("Restaurant is currently unavailable");
    } else {
      throw new Error("Failed to fetch order details. Please try again.");
    }
  }
};

/**
 * Complete restaurant creation data interface
 */
export interface RestaurantCreationData {
  //  Basic Information (Required)
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  location?: string;
  website?: string;

  // Customer-Facing Marketing Information
  tagline?: string;
  description?: string;
  cuisineType?: string;
  priceRange?: "₹" | "₹₹" | "₹₹₹" | "₹₹₹₹";
  openingHours?: string;

  // Service Information
  deliveryTime?: number;
  deliveryFee?: number;
  minOrderAmount?: number;
  
  // UI preferences
  template?: string;

  //  Features & Amenities
  specialFeatures?: string;

  // Visual Assets (Files)
  logo?: File;
  aboutImage?: File;
  bannerImage?: File;
  images?: File[];
  removedGalleryImages?: string[];
  featuredDishIds?: number[];
  // Offers and features (sent as JSON strings in multipart)
  offers?: Array<{
    title: string;
    description?: string;
    promoCode?: string;
    isActive?: boolean;
    validFrom?: string;
    validUntil?: string;
  }>;
  features?: {
    wifi?: boolean;
    parking?: boolean;
    delivery?: boolean;
    takeaway?: boolean;
  };
  // Social media links
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
}

/**
 * Restaurant creation response
 */
export interface RestaurantCreationResponse {
  id: number;
  name: string;
  message: string;
  success: boolean;
}

/**
 * Create a new restaurant with complete information including images
 * Supports multipart/form-data for file uploads
 */
export const createRestaurant = async (
  restaurantData: RestaurantCreationData
): Promise<RestaurantCreationResponse> => {
  try {
    const formData = new FormData();

    // Basic Information (Required)
    formData.append("name", restaurantData.name);

    // Contact Information
    if (restaurantData.email) formData.append("email", restaurantData.email);
    if (restaurantData.phone) formData.append("phone", restaurantData.phone);
    if (restaurantData.address)
      formData.append("address", restaurantData.address);
    if (restaurantData.website)
      formData.append("website", restaurantData.website);

    // Marketing Information
    if (restaurantData.tagline)
      formData.append("tagline", restaurantData.tagline);
    if (restaurantData.description)
      formData.append("description", restaurantData.description);
    if (restaurantData.template)
      formData.append("template", restaurantData.template);

    if (restaurantData.priceRange)
      formData.append("priceRange", restaurantData.priceRange);
    if (restaurantData.openingHours)
      formData.append("openingHours", restaurantData.openingHours);

    // Location link
    if (restaurantData.location) formData.append("location", restaurantData.location);

    //  Service Information
    if (restaurantData.deliveryTime !== undefined) {
      formData.append("deliveryTime", restaurantData.deliveryTime.toString());
    }
    if (restaurantData.deliveryFee !== undefined) {
      formData.append("deliveryFee", restaurantData.deliveryFee.toString());
    }
    if (restaurantData.minOrderAmount !== undefined) {
      formData.append(
        "minOrderAmount",
        restaurantData.minOrderAmount.toString()
      );
    }

    // Features
    if (restaurantData.specialFeatures)
      formData.append("specialFeatures", restaurantData.specialFeatures);

    // Visual Assets
    if (restaurantData.logo) formData.append("logo", restaurantData.logo);
    if (restaurantData.bannerImage)
      formData.append("bannerImage", restaurantData.bannerImage);
    if (restaurantData.images) {
      restaurantData.images.forEach((image) =>
        formData.append("GalleryImages", image)
      );
    }
    if (restaurantData.aboutImage) formData.append('aboutImage', restaurantData.aboutImage);
    if (restaurantData.offers) {
      formData.append('offers', JSON.stringify(restaurantData.offers));
    }
    if (restaurantData.features) {
      formData.append('features', JSON.stringify(restaurantData.features));
    }
    if ((restaurantData as any).socialMedia !== undefined) {
      const sm = (restaurantData as any).socialMedia || {};
      formData.append('socialMedia', JSON.stringify(sm));
      if (sm.facebook) {
        formData.append('facebook', sm.facebook);
        formData.append('socialMedia.Facebook', sm.facebook);
      }
      if (sm.instagram) {
        formData.append('instagram', sm.instagram);
        formData.append('socialMedia.Instagram', sm.instagram);
      }
      if (sm.twitter) {
        formData.append('twitter', sm.twitter);
        formData.append('socialMedia.Twitter', sm.twitter);
      }
    }
    if ((restaurantData as any).removedGalleryImages) {
      formData.append(
        "removedGalleryImages",
        JSON.stringify((restaurantData as any).removedGalleryImages)
      );
    }

    const response = await axiosInstance.post<
      ApiResponse<RestaurantCreationResponse>
    >("/api/Restaurants/create", formData);

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to create restaurant");
    }
  } catch (error: any) {
    console.error("Error creating restaurant:", error);

    // Handle specific error cases
    if (error.response) {
      const status = error.response.status;
      const message =
        error.response.data?.message || error.response.data?.error;

      if (status === 400) {
        if (message && message.includes("Restaurant limit reached")) {
          throw new Error(
            " You have reached the maximum number of restaurants allowed for your current plan. Please upgrade your plan to create more restaurants."
          );
        } else if (message && message.includes("limit")) {
          throw new Error(
            ` ${message} Please check your subscription plan limits.`
          );
        } else {
          throw new Error(message || "Invalid restaurant data provided");
        }
      } else if (status === 401) {
        throw new Error("Authentication required to create restaurant");
      } else if (status === 403) {
        throw new Error(
          "You do not have permission to create restaurants. Please check your subscription plan."
        );
      } else if (status === 409) {
        throw new Error("Restaurant name already exists");
      } else if (status === 422) {
        throw new Error(
          "Restaurant data validation failed. Please check your inputs."
        );
      } else if (status === 500) {
        throw new Error("Server error occurred. Please try again later.");
      } else {
        throw new Error(message || `Something went wrong. Try again.`);
      }
    } else if (
      error.code === "ERR_NETWORK" ||
      error.message.includes("Network Error")
    ) {
      throw new Error(
        "Unable to connect to server. Please check your internet connection and try again."
      );
    } else {
      throw new Error(
        error.message || "Failed to create restaurant. Please try again."
      );
    }
  }
};

/**
 * Update restaurant using token-based authentication (no ID required in URL)
 * Automatically detects which restaurant to update based on user's token
 */
export const updateRestaurant = async (
  restaurantData: Partial<RestaurantCreationData>
): Promise<RestaurantCreationResponse> => {
  try {
    const formData = new FormData();

    // Always append all text fields (including empty strings) to ensure proper updates
    if (restaurantData.name !== undefined)
      formData.append("name", restaurantData.name);
    if (restaurantData.email !== undefined)
      formData.append("email", restaurantData.email || "");
    if (restaurantData.phone !== undefined)
      formData.append("phone", restaurantData.phone || "");
    if (restaurantData.address !== undefined)
      formData.append("address", restaurantData.address || "");
    if (restaurantData.website !== undefined)
      formData.append("website", restaurantData.website || "");

    if (restaurantData.tagline !== undefined)
      formData.append("tagline", restaurantData.tagline || "");
    if (restaurantData.description !== undefined)
      formData.append("description", restaurantData.description || "");
    if (restaurantData.template !== undefined)
      formData.append("template", restaurantData.template || "");
    if (restaurantData.priceRange !== undefined)
      formData.append("priceRange", restaurantData.priceRange || "");
    if (restaurantData.openingHours !== undefined)
      formData.append("openingHours", restaurantData.openingHours || "");
    if (restaurantData.location !== undefined)
      formData.append("location", restaurantData.location || "");

    if (restaurantData.deliveryTime !== undefined) {
      formData.append("deliveryTime", restaurantData.deliveryTime.toString());
    }
    if (restaurantData.deliveryFee !== undefined) {
      formData.append("deliveryFee", restaurantData.deliveryFee.toString());
    }
    if (restaurantData.minOrderAmount !== undefined) {
      formData.append(
        "minOrderAmount",
        restaurantData.minOrderAmount.toString()
      );
    }

    if (restaurantData.specialFeatures !== undefined)
      formData.append("specialFeatures", restaurantData.specialFeatures || "");

    if (restaurantData.logo) formData.append("logo", restaurantData.logo);
    if (restaurantData.bannerImage)
      formData.append("bannerImage", restaurantData.bannerImage);
    if (restaurantData.images) {
      restaurantData.images.forEach((image) =>
        formData.append("GalleryImages", image)
      );
    }
    if (restaurantData.aboutImage) formData.append('aboutImage', restaurantData.aboutImage);
    if (restaurantData.offers) {
      formData.append('offers', JSON.stringify(restaurantData.offers));
    }
    if (restaurantData.features) {
      formData.append('features', JSON.stringify(restaurantData.features));
    }
    if (restaurantData.featuredDishIds) {
      restaurantData.featuredDishIds.forEach((id) =>
        formData.append("featuredDishIds", id.toString())
      );
    }

    if ((restaurantData as any).socialMedia !== undefined) {
      const sm = (restaurantData as any).socialMedia || {};
      formData.append('socialMedia', JSON.stringify(sm));
      if (sm.facebook) {
        formData.append('facebook', sm.facebook);
        formData.append('socialMedia.Facebook', sm.facebook);
      }
      if (sm.instagram) {
        formData.append('instagram', sm.instagram);
        formData.append('socialMedia.Instagram', sm.instagram);
      }
      if (sm.twitter) {
        formData.append('twitter', sm.twitter);
        formData.append('socialMedia.Twitter', sm.twitter);
      }
    }

    // Debug: Log FormData contents
    const formDataEntries: any = {};
    formData.forEach((value, key) => {
      if (value instanceof File) {
        formDataEntries[key] = `File: ${value.name} (${value.size} bytes)`;
      } else {
        formDataEntries[key] = value;
      }
    });

    const response = await axiosInstance.put<
      ApiResponse<RestaurantCreationResponse>
    >(
      "/api/Restaurants/update", // Token-based endpoint - no ID needed
      formData
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to update restaurant");
    }
  } catch (error: any) {
    console.error("Error updating restaurant:", error);

    if (error.response?.status === 400) {
      throw new Error(
        error.response.data?.message || "Invalid restaurant data provided"
      );
    } else if (error.response?.status === 401) {
      throw new Error("Authentication required to update restaurant");
    } else if (error.response?.status === 403) {
      throw new Error("You do not have permission to update this restaurant");
    } else if (error.response?.status === 404) {
      throw new Error("Restaurant not found");
    } else {
      throw new Error("Failed to update restaurant. Please try again.");
    }
  }
};

/**
 * Update restaurant by ID. This endpoint updates only provided fields
 * and targets a specific restaurant using its ID.
 */
export const updateRestaurantById = async (
  restaurantId: number,
  restaurantData: Partial<RestaurantCreationData>
): Promise<RestaurantCreationResponse> => {
  try {
    const formData = new FormData();

    if (restaurantData.name !== undefined)
      formData.append("name", restaurantData.name);
    if (restaurantData.email !== undefined)
      formData.append("email", restaurantData.email || "");
    if (restaurantData.phone !== undefined)
      formData.append("phone", restaurantData.phone || "");
    if (restaurantData.address !== undefined)
      formData.append("address", restaurantData.address || "");
    if (restaurantData.website !== undefined)
      formData.append("website", restaurantData.website || "");

    if (restaurantData.tagline !== undefined)
      formData.append("tagline", restaurantData.tagline || "");
    if (restaurantData.description !== undefined)
      formData.append("description", restaurantData.description || "");
    if (restaurantData.template !== undefined)
      formData.append("template", restaurantData.template || "");
    if (restaurantData.priceRange !== undefined)
      formData.append("priceRange", restaurantData.priceRange || "");
    if (restaurantData.openingHours !== undefined)
      formData.append("openingHours", restaurantData.openingHours || "");
    if (restaurantData.location !== undefined)
      formData.append("location", restaurantData.location || "");

    if (restaurantData.deliveryTime !== undefined) {
      formData.append("deliveryTime", restaurantData.deliveryTime.toString());
    }
    if (restaurantData.deliveryFee !== undefined) {
      formData.append("deliveryFee", restaurantData.deliveryFee.toString());
    }
    if (restaurantData.minOrderAmount !== undefined) {
      formData.append(
        "minOrderAmount",
        restaurantData.minOrderAmount.toString()
      );
    }

    if (restaurantData.specialFeatures !== undefined)
      formData.append("specialFeatures", restaurantData.specialFeatures || "");

    if (restaurantData.logo) formData.append("logo", restaurantData.logo);
    if (restaurantData.bannerImage)
      formData.append("bannerImage", restaurantData.bannerImage);
    if (restaurantData.images) {
      restaurantData.images.forEach((image) =>
        formData.append("GalleryImages", image)
      );
    }
    if ((restaurantData as any).aboutImage) formData.append('aboutImage', (restaurantData as any).aboutImage);
    if ((restaurantData as any).offers) {
      formData.append('offers', JSON.stringify((restaurantData as any).offers));
    }
    if ((restaurantData as any).features) {
      formData.append('features', JSON.stringify((restaurantData as any).features));
    }
    if ((restaurantData as any).removedGalleryImages) {
      formData.append(
        "removedGalleryImages",
        JSON.stringify((restaurantData as any).removedGalleryImages)
      );
    }
    if ((restaurantData as any).socialMedia !== undefined) {
      const sm = (restaurantData as any).socialMedia || {};
      formData.append('socialMedia', JSON.stringify(sm));
      if (sm.facebook) {
        formData.append('facebook', sm.facebook);
        formData.append('socialMedia.Facebook', sm.facebook);
      }
      if (sm.instagram) {
        formData.append('instagram', sm.instagram);
        formData.append('socialMedia.Instagram', sm.instagram);
      }
      if (sm.twitter) {
        formData.append('twitter', sm.twitter);
        formData.append('socialMedia.Twitter', sm.twitter);
      }
    }
    if (restaurantData.featuredDishIds) {
      restaurantData.featuredDishIds.forEach((id) =>
        formData.append("featuredDishIds", id.toString())
      );
    }

    const response = await axiosInstance.put<
      ApiResponse<RestaurantCreationResponse>
    >(`/api/Restaurants/${restaurantId}/update`, formData);

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to update restaurant");
    }
  } catch (error: any) {
    console.error("Error updating restaurant by id:", error);

    if (error.response?.status === 400) {
      throw new Error(
        error.response.data?.message || "Invalid restaurant data provided"
      );
    } else if (error.response?.status === 401) {
      throw new Error("Authentication required to update restaurant");
    } else if (error.response?.status === 403) {
      throw new Error("You do not have permission to update this restaurant");
    } else if (error.response?.status === 404) {
      throw new Error("Restaurant not found");
    } else {
      throw new Error("Failed to update restaurant. Please try again.");
    }
  }
};

/**
 * Get all restaurants owned by current user (token-based)
 */
export const getMyRestaurants = async (): Promise<RestaurantDetails[]> => {
  try {
    const response = await axiosInstance.get<ApiResponse<RestaurantDetails[]>>(
      "/api/Restaurants/my-restaurants"
    );


    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch restaurants");
    }
  } catch (error: any) {
    console.error("❌ Error in getMyRestaurants:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.response?.data?.message || error.message,
      url: error.config?.url,
    });

    if (error.response?.status === 401) {
      throw new Error("Authentication required");
    } else if (error.response?.status === 404) {
      throw new Error(
        "No restaurants found for your account. Please create one first."
      );
    } else {
      throw new Error("Failed to fetch restaurants");
    }
  }
};

/**
 * Get current user's restaurant (token-based) - single restaurant
 * Automatically extracts restaurant from token
 * - Owners: gets their owned restaurant
 * - Managers: gets restaurant from restaurantId claim
 */
export const getMyRestaurant = async (): Promise<RestaurantDetails> => {
  try {
    const response = await axiosInstance.get<ApiResponse<RestaurantDetails>>(
      "/api/restaurants/my-restaurant"
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch restaurant");
    }
  } catch (error: any) {
    console.error("Error fetching my restaurant:", error);

    if (error.response?.status === 401) {
      throw new Error("Authentication required");
    } else if (error.response?.status === 404) {
      throw new Error(
        "No restaurant found for your account. Please create one first."
      );
    } else {
      throw new Error("Failed to fetch restaurant");
    }
  }
};

export interface FeaturedDish {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string;
  price: number;
  isAvailable?: boolean;
  canBePrepared?: boolean;
  availableServings?: number | null;
  metadata?: {
    isVegetarian?: boolean;
    isVegan?: boolean;
    isSpicy?: boolean;
    allergens?: string;
    calories?: number;
  };
}

export interface RestaurantSearchResult {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  isActive: boolean;
  logo?: string;
  cuisineType?: string;
  priceRange?: string;
  averageRating: number;
  galleryImages?: string[];
  featuredDishes: FeaturedDish[];
  tagline?: string;
  description?: string;
  openingHours?: string;
  deliveryTime?: number;
  deliveryFee?: number;
  minOrderAmount?: number;
  specialFeatures?: string;
  bannerImage?: string;
  hasTableService: boolean;
  hasOnlineOrdering: boolean;
  displayName: string;
}

export interface PaginatedRestaurantResult {
  restaurants: RestaurantSearchResult[];
  totalResults: number;
  currentPage: number;
  totalPages: number;
}

/**
 * Search for restaurants
 */
export const searchRestaurants = async (
  searchTerm?: string,
  pageNumber: number = 1,
  pageSize: number = 20
): Promise<PaginatedRestaurantResult> => {
  try {
    const response = await axiosInstance.get<
      ApiResponse<PaginatedRestaurantResult>
    >(`/api/public/restaurants/search`, {
      params: {
        searchTerm,
        pageNumber,
        pageSize,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to search restaurants");
    }
  } catch (error: any) {
    console.error("Error searching restaurants:", error);
    throw new Error("Failed to search restaurants");
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// RESTAURANT TOP-UP MODULES APIs
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get restaurant subscription details with plan modules
 */
export const getRestaurantSubscription = async (
  restaurantId: number
): Promise<{ planModules: string[]; planName: string }> => {
  try {
    const response = await axiosInstance.get<ApiResponse<any>>(
      `/api/restaurants/${restaurantId}/subscription`
    );

    if (response.data.success && response.data.data) {
      return {
        planModules: response.data.data.planModules || response.data.data.includedModules || [],
        planName: response.data.data.planName || "Unknown Plan"
      };
    } else {
      return { planModules: [], planName: "No Plan" };
    }
  } catch (error: any) {
    console.error("Error fetching restaurant subscription:", error);
    return { planModules: [], planName: "No Plan" };
  }
};

/**
 * Get restaurant's top-up modules
 */
export const getRestaurantTopUpModules = async (
  restaurantId: number
): Promise<RestaurantTopUpResponse> => {
  try {
    const response = await axiosInstance.get<ApiResponse<RestaurantTopUpResponse>>(
      `/api/restaurants/${restaurantId}/topup-modules`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to fetch top-up modules");
    }
  } catch (error: any) {
    console.error("Error fetching top-up modules:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch top-up modules");
  }
};

/**
 * Add top-up modules to restaurant
 */
export const addRestaurantTopUpModules = async (
  restaurantId: number,
  moduleKeys: string[]
): Promise<RestaurantTopUpResponse> => {
  try {
    const response = await axiosInstance.post<ApiResponse<RestaurantTopUpResponse>>(
      `/api/restaurants/${restaurantId}/topup-modules`,
      { moduleKeys }
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to add top-up modules");
    }
  } catch (error: any) {
    console.error("Error adding top-up modules:", error);
    throw new Error(error.response?.data?.message || "Failed to add top-up modules");
  }
};

/**
 * Remove top-up modules from restaurant
 */
export const removeRestaurantTopUpModules = async (
  restaurantId: number,
  moduleKeys: string[]
): Promise<RestaurantTopUpResponse> => {
  try {
    const response = await axiosInstance.delete<ApiResponse<RestaurantTopUpResponse>>(
      `/api/restaurants/${restaurantId}/topup-modules`,
      { data: { moduleKeys } }
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || "Failed to remove top-up modules");
    }
  } catch (error: any) {
    console.error("Error removing top-up modules:", error);
    throw new Error(error.response?.data?.message || "Failed to remove top-up modules");
  }
};
